import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../contexts/NotificationContext';
import socketClient from '../utils/socketClient';
import { useAuth } from '../contexts/AuthContext';

export const useOfferNotifications = () => {
  const { showOfferNotification, showSuccess, showError } = useNotifications();
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) return;

    // New offer notification for sellers
    const handleNewOfferNotification = (data) => {
      if (data.offer && data.offer.sellerId._id === user._id) {
        showOfferNotification('new_offer', {
          buyerName: data.offer.buyerId.username,
          amount: data.offer.offerAmount,
          productName: data.offer.productId.name,
          action: () => {
            // Navigate to received offers page
            navigate('/received-offers');
          }
        });
      }
    };

    // Offer accepted notification for buyers
    const handleOfferAccepted = (data) => {
      if (data.offer && data.offer.buyerId._id === user._id) {
        showOfferNotification('offer_accepted', {
          amount: data.offer.offerAmount,
          productName: data.offer.productId.name,
          action: () => {
            // Navigate to my offers page
            navigate('/my-offers');
          }
        });
      }
    };

    // Offer rejected notification for buyers
    const handleOfferRejected = (data) => {
      if (data.offer && data.offer.buyerId._id === user._id) {
        showOfferNotification('offer_rejected', {
          productName: data.offer.productId.name
        });
      }
    };

    // Fee payment required notification
    const handleFeePaymentRequired = (data) => {
      if (data.offer && data.offer.buyerId._id === user._id) {
        showOfferNotification('fee_payment_required', {
          amount: data.feeAmount,
          action: () => {
            // Navigate to my offers page to pay fee
            navigate('/my-offers');
          }
        });
      }
    };

    // Fee paid / Chat unlocked notification
    const handleFeePaid = (data) => {
      const { party, bothPaid } = data;
      const otherParty = party === 'buyer' ? 'seller' : 'buyer';
      const statusMsg = bothPaid ? 'Chat unlocked' : `${party} paid, waiting for ${otherParty}`;
      showOfferNotification('fee_paid', {
        userType: party,
        productName: data.offer?.productId?.name || 'this product',
        extra: statusMsg,
        action: () => {
          const page = data.offer?.buyerId?._id === user._id ? '/my-offers' : '/received-offers';
          navigate(page);
        }
      });
    };

    // Chat started notification
    const handleChatStarted = (data) => {
      showSuccess(`Chat is now available for "${data.offer?.productId?.name || 'your product'}"`);
    };

    // Chat message notification (when user is not in chat)
    const handleChatMessage = (data) => {
      // Only show notification if message is not from current user
      if (data.message && data.message.senderId._id !== user._id) {
        const senderName = data.message.senderId.username || 
                          `${data.message.senderId.profile?.firstName || ''} ${data.message.senderId.profile?.lastName || ''}`.trim() ||
                          'Someone';
        
        showOfferNotification('chat_message', {
          senderName,
          message: data.message.content.length > 50 ? 
                   data.message.content.substring(0, 50) + '...' : 
                   data.message.content,
          action: () => {
            // This could open the chat modal or navigate to the appropriate page
            // For now, just navigate to offers page
            const page = data.message.senderId._id !== user._id ? '/my-offers' : '/received-offers';
            navigate(page);
          }
        });
      }
    };

    // Offer withdrawal notification
    const handleOfferWithdrawn = (data) => {
      if (data.offer && data.offer.sellerId._id === user._id) {
        showOfferNotification('offer_withdrawn', {
          productName: data.offer.productId.name
        });
      }
    };

    // Register socket event listeners
    socketClient.on('new_offer_notification', handleNewOfferNotification);
    socketClient.on('offer_accepted', handleOfferAccepted);
    socketClient.on('offer_rejected', handleOfferRejected);
    socketClient.on('fee_payment_required', handleFeePaymentRequired);
    socketClient.on('fee_paid', handleFeePaid);
    socketClient.on('chat_started', handleChatStarted);
    socketClient.on('chat_message', handleChatMessage);
    socketClient.on('offer_withdrawn', handleOfferWithdrawn);

    // Cleanup
    return () => {
      socketClient.off('new_offer_notification', handleNewOfferNotification);
      socketClient.off('offer_accepted', handleOfferAccepted);
      socketClient.off('offer_rejected', handleOfferRejected);
      socketClient.off('fee_payment_required', handleFeePaymentRequired);
      socketClient.off('fee_paid', handleFeePaid);
      socketClient.off('chat_started', handleChatStarted);
      socketClient.off('chat_message', handleChatMessage);
      socketClient.off('offer_withdrawn', handleOfferWithdrawn);
    };
  }, [user, showOfferNotification, showSuccess, showError, navigate]);
};
